/**
 * Fetch Qdrant facet values for a declared external-chat filter field.
 *
 * Authenticates with an embed API key (eak_...). The platform-api endpoint
 * validates the key against the target collection and refuses fields that
 * aren't declared in `collection.external_chat_filters`.
 *
 * Returns the list of unique values for the field so the iframe can render
 * live filter options without baking stale values into the embed URL.
 */

import * as Sentry from '@sentry/react';

export interface FacetHit {
  value: string;
  count: number;
}

export interface CollectionFacetResponse {
  hits: FacetHit[];
}

export interface GetExternalChatFacetParams {
  orgId: string;
  collectionId: string;
  apiKey: string;
  field: string;
  limit?: number;
  exact?: boolean;
  signal?: AbortSignal;
}

export async function getExternalChatFacet({
  orgId,
  collectionId,
  apiKey,
  field,
  limit = 100,
  exact = false,
  signal,
}: GetExternalChatFacetParams): Promise<CollectionFacetResponse> {
  const params = new URLSearchParams({
    field,
    limit: String(limit),
    exact: String(exact),
  });

  const url = `/proxy/platform/${encodeURIComponent(orgId)}/collections/${encodeURIComponent(collectionId)}/external-chat/facet?${params.toString()}`;

  try {
    const response = await fetch(url, {
      method: 'GET',
      headers: {
        Authorization: `Bearer ${apiKey}`,
      },
      signal,
    });

    if (!response.ok) {
      const errText = await response.text().catch(() => '');
      let detail = `Facet request failed (${response.status})`;
      try {
        const parsed = JSON.parse(errText);
        detail = parsed.detail || parsed.error || parsed.message || detail;
      } catch {
        if (errText) detail = errText.slice(0, 300);
      }
      throw new Error(detail);
    }

    const data = (await response.json()) as CollectionFacetResponse;
    return data;
  } catch (err) {
    if (signal?.aborted) throw err;
    Sentry.captureException(err, {
      extra: {
        context: 'get-external-chat-facet',
        orgId,
        collectionId,
        field,
      },
    });
    throw err;
  }
}
